import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../hooks/useAuth";
import { tenantService } from "../../services/tenantService";
import type { AppRole } from "../../types/auth";
import type {
  Tenant,
  TenantContextSnapshot,
  TenantMembershipWithTenant,
} from "../../types/tenant";

type TenantContextValue = TenantContextSnapshot & {
  loading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
};

export const TenantContext = createContext<TenantContextValue | undefined>(
  undefined,
);

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unable to load workspace.";
}

const EMPTY_SNAPSHOT: TenantContextSnapshot = {
  memberships: [],
  currentMembership: null,
  currentTenant: null,
  currentRole: null,
};

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, isConfigured } = useAuth();
  const [memberships, setMemberships] = useState<TenantMembershipWithTenant[]>(
    [],
  );
  const [currentMembership, setCurrentMembership] =
    useState<TenantMembershipWithTenant | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [currentRole, setCurrentRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applySnapshot = useCallback(
    (nextMemberships: TenantMembershipWithTenant[]) => {
      const primary = tenantService.resolvePrimaryMembership(nextMemberships);

      setMemberships(nextMemberships);
      setCurrentMembership(primary);
      setCurrentTenant(primary?.tenants ?? null);
      setCurrentRole((primary?.role as AppRole | undefined) ?? null);
    },
    [],
  );

  const resetSnapshot = useCallback(() => {
    setMemberships(EMPTY_SNAPSHOT.memberships);
    setCurrentMembership(EMPTY_SNAPSHOT.currentMembership);
    setCurrentTenant(EMPTY_SNAPSHOT.currentTenant);
    setCurrentRole(EMPTY_SNAPSHOT.currentRole);
  }, []);

  const loadTenant = useCallback(async () => {
    if (!isConfigured || !user) {
      resetSnapshot();
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextMemberships = await tenantService.getMembershipsForUser(user.id);

      applySnapshot(nextMemberships);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError));
      resetSnapshot();
    } finally {
      setLoading(false);
    }
  }, [applySnapshot, isConfigured, resetSnapshot, user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void loadTenant();
  }, [authLoading, loadTenant]);

  const value = useMemo<TenantContextValue>(
    () => ({
      memberships,
      currentMembership,
      currentTenant,
      currentRole,
      loading,
      error,
      refreshTenant: loadTenant,
    }),
    [
      currentMembership,
      currentRole,
      currentTenant,
      error,
      loadTenant,
      loading,
      memberships,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}
